import { computeLos } from "@/tactical/los/computeLos";
import { destinationLngLat } from "./destinationLngLat";

export type ViewshedResult = {
  observer: { lng: number; lat: number };
  radiusM: number;
  aglM: number;
  usedTerrain: boolean;
  /** Замкнутое кольцо полигона [lng, lat][]. */
  ring: [number, number][];
  flatEarth: boolean;
};

/**
 * Radial viewshed: для каждого азимута — LOS до края радиуса; вершина = точка блокировки или край.
 */
export function computeViewshed(opts: {
  observer: { lng: number; lat: number };
  radiusM: number;
  aglM: number;
  sampleGroundM: (lng: number, lat: number) => number | null;
  bearings?: number;
  radialSteps?: number;
  epsM?: number;
}): ViewshedResult {
  const radiusM = Math.max(10, Math.min(50_000, opts.radiusM));
  const bearings = Math.max(16, Math.min(180, opts.bearings ?? 72));
  const radialSteps = Math.max(8, Math.min(128, opts.radialSteps ?? 32));
  const aglM = Math.max(0, opts.aglM);
  const epsM = opts.epsM ?? 0.5;

  let usedTerrain = false;
  const sample = (lng: number, lat: number): number | null => {
    const v = opts.sampleGroundM(lng, lat);
    if (v != null && Number.isFinite(v)) usedTerrain = true;
    return v;
  };

  const ring: [number, number][] = [];
  for (let i = 0; i < bearings; i++) {
    const bearing = (360 * i) / bearings;
    const edge = destinationLngLat(
      opts.observer.lng,
      opts.observer.lat,
      bearing,
      radiusM,
    );
    const los = computeLos(
      { lng: opts.observer.lng, lat: opts.observer.lat, aglM },
      { lng: edge.lng, lat: edge.lat, aglM: 0 },
      sample,
      radialSteps,
      epsM,
    );
    if (los.usedTerrain) usedTerrain = true;
    if (los.blockAt) {
      ring.push([los.blockAt.lng, los.blockAt.lat]);
    } else {
      ring.push([edge.lng, edge.lat]);
    }
  }
  if (ring.length > 0) {
    ring.push(ring[0]!);
  }

  return {
    observer: opts.observer,
    radiusM,
    aglM,
    usedTerrain,
    ring,
    flatEarth: !usedTerrain,
  };
}
